<template>
  <section class="signs-process" aria-labelledby="firma-escrituras-process-title">
    <DsChip label="Tu proceso" variant="orange" />
    <div class="signs-process__heading">
      <h2 id="firma-escrituras-process-title">¿Cómo es el <span>proceso</span>?</h2>
      <p>Tu camino está dividido en etapas claras. Aquí puedes entender qué sigue:</p>
    </div>

    <div class="signs-process__cards">
      <article v-for="(step, index) in processSteps" :key="step.title" class="signs-process__card">
        <header class="signs-process__card-heading">
          <span>{{ index + 1 }}</span>
          <h3>{{ step.title }}</h3>
        </header>
        <div class="signs-process__card-content">
          <p v-if="step.description" :class="{ 'signs-process__default-copy': step.desktopContent }">{{ step.description }}</p>
          <ul v-if="step.bullets" :class="{ 'signs-process__default-copy': step.desktopContent }">
            <li v-for="bullet in step.bullets" :key="bullet">{{ bullet }}</li>
          </ul>
          <div v-if="step.desktopContent" class="signs-process__desktop-copy">
            <p>{{ step.desktopContent.intro }}</p>
            <ul>
              <li v-for="bullet in step.desktopContent.bullets" :key="bullet">{{ bullet }}</li>
            </ul>
            <p>{{ step.desktopContent.outro }}</p>
          </div>
        </div>
      </article>
    </div>
  </section>
</template>

<script setup lang="ts">
import DsChip from '../atoms/DsChip.vue'

interface ProcessStep {
  title: string
  description?: string
  bullets?: string[]
  desktopContent?: {
    intro: string
    bullets: string[]
    outro: string
  }
}

const processSteps: ProcessStep[] = [
  {
    title: 'Acompañamiento y asesoría',
    description: 'Nuestro equipo de asesores y expertos estará encantado de brindarte toda la información necesaria y resolver tus dudas, haciendo el proceso más sencillo, agradable y seguro.',
    desktopContent: {
      intro: 'Evaluaremos tu historial, capacidad de pago y endeudamiento mediante:',
      bullets: ['Buró de crédito', 'Comprobantes de ingresos'],
      outro: 'Esto nos permite identificar la mejor alternativa para tu proceso.'
    }
  },
  {
    title: 'Preparación y asistencia a la cita',
    bullets: [
      'Es vital que todas las partes involucradas en la operación estén presentes en la notaría.',
      'Recuerda llevar tus documentos originales de identificación oficial (INE o pasaporte) y CURP, ambos vigentes y actualizados. Tu ejecutivo asignado te ayudará a revisar detalladamente los lineamientos para una transacción ágil.'
    ]
  },
  {
    title: 'Emisión y entrega',
    description: 'Una vez que hayas firmado la escritura en la fecha programada, recibirás de inmediato el comprobante de pago correspondiente al trámite realizado. ¡Estás a punto de disfrutar plenamente de tu patrimonio!'
  }
]
</script>

<style scoped>
.signs-process { display: flex; flex-direction: column; gap: 24px; overflow: hidden; padding: 24px 16px; border-radius: 24px; background: var(--ds-grey-50); }
.signs-process :deep(.ds-chip) { padding: 8px; color: var(--ds-orange-800); background: var(--ds-orange-50); font-size: var(--ds-subtitle9-size); line-height: 16px; }
.signs-process__heading { display: flex; flex-direction: column; align-items: flex-start; gap: 16px; }
.signs-process h2 { margin: 0; color: var(--ds-purple-900); font-family: var(--ds-font-family-bebas); font-size: 40px; line-height: 44px; font-weight: 700; }
.signs-process h2 span { color: var(--ds-purple-400); }
.signs-process p { margin: 0; color: var(--ds-text-primary); font-size: var(--ds-body3-size); line-height: 24px; }
.signs-process__cards { display: flex; flex-direction: column; gap: 16px; width: 100%; }
.signs-process__card { display: flex; flex-direction: column; padding: 16px; border: 1px solid var(--ds-purple-300); border-radius: 8px; background: #fbfbfb; }
.signs-process__card-heading { display: flex; align-items: center; gap: 16px; padding-bottom: 16px; border-bottom: 1px solid var(--ds-grey-200); }
.signs-process__card-heading > span { display: grid; flex: 0 0 40px; width: 40px; height: 40px; place-items: center; border-radius: 50%; background: var(--ds-purple-50); color: var(--ds-purple-900); font-family: var(--ds-font-family-bebas); font-size: 24px; line-height: 28px; font-weight: 700; }
.signs-process__card-content { padding-top: 16px; }
.signs-process__card h3 { margin: 0; color: var(--ds-purple-900); font-size: 16px; line-height: 20px; font-weight: 600; }
.signs-process__card p { font-size: 14px; line-height: 20px; }
.signs-process__card ul { display: flex; flex-direction: column; gap: 8px; margin: 0; padding-left: 20px; color: var(--ds-text-primary); font-size: 14px; line-height: 20px; }
.signs-process__desktop-copy { display: none; }

@media (min-width: 768px) {
  .signs-process { gap: 40px; padding: 32px; }
  .signs-process__heading { gap: 8px; }
  .signs-process__cards { gap: 24px; }
  .signs-process__card { height: 100%; padding: 24px; box-sizing: border-box; }
  .signs-process__card-heading { padding-bottom: 20px; }
  .signs-process__card-content { padding-top: 20px; }
  .signs-process__card h3 { font-size: 18px; line-height: 24px; }
  .signs-process__card p, .signs-process__card ul { font-size: 16px; line-height: 24px; }
  .signs-process__card ul { gap: 24px; }
}

@media (min-width: 1024px) {
  .signs-process__cards { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 24px 28px; }
  .signs-process__card:last-child { grid-column: 1 / -1; }
  .signs-process__default-copy { display: none; }
  .signs-process__desktop-copy { display: flex; flex-direction: column; gap: 24px; }
  .signs-process__desktop-copy ul { gap: 0; }
}
</style>
